import { AxiosResponse } from 'axios';

import { axiosAuthInstance } from 'api/base';
import { prepareFormData } from 'utils/form';

import {
  normalizePermission,
  normalizePermissionPicture,
  normalizeRole,
} from './normalizers';
import {
  IDeleteRoleForm,
  IPermission,
  IPermissionForm,
  IPermissionPicture,
  IRole,
  IRolePayload,
  IRolesParams,
  IUpdateRolePayload,
} from './types';

const rolesPermissionsAPI = {
  async getRoles(params?: IRolesParams): Promise<IRole[]> {
    const response = await axiosAuthInstance.get('roles', { params });

    return response?.data?.map(normalizeRole);
  },

  async getPermissions(): Promise<IPermission[]> {
    const response = await axiosAuthInstance.get('permissions');

    return response?.data?.result?.map(normalizePermission) || [];
  },

  async getRole(id: string): Promise<IRole> {
    const response = await axiosAuthInstance.get(`roles/${id}`);

    return normalizeRole(response.data);
  },

  async createRole(payload: IRolePayload): Promise<AxiosResponse> {
    const fromPayload = prepareFormData(payload);
    const response = await axiosAuthInstance.post('roles', fromPayload);

    return response;
  },

  async deleteRole({ assignRoleId, roleId }: IDeleteRoleForm): Promise<AxiosResponse> {
    const getParams = () => {
      if (!assignRoleId) {
        return {};
      }

      return { params: { assignRoleId } };
    };

    const response = await axiosAuthInstance.delete(`roles/${roleId}`, getParams());

    return response.data;
  },

  async updateRole({ id, ...payload }: IUpdateRolePayload): Promise<AxiosResponse> {
    const fromPayload = prepareFormData(payload);
    const response = await axiosAuthInstance.put(`roles/${id}`, fromPayload);

    return response;
  },

  async getPermissionPicture(permission: string): Promise<IPermissionPicture> {
    const response = await axiosAuthInstance.get(`permissions/${permission}/picture`);

    return normalizePermissionPicture(response.data);
  },

  async updatePermission(props: IPermissionForm): Promise<AxiosResponse> {
    const { permission, ...payload } = props;

    const fromPayload = prepareFormData(payload);
    const response = await axiosAuthInstance.put(
      `permissions/${permission}/picture`,
      fromPayload,
    );

    return response;
  },

  async deletePermissionInfo(permission: string): Promise<AxiosResponse> {
    const response = await axiosAuthInstance.delete(`permissions/${permission}/picture`);

    return response.data;
  },
};

export default rolesPermissionsAPI;
